/**
 * Mate management module (in-room).
 *
 * Handles adding and removing mates from a room.
 */

import type { RoomStorage, BroadcastChannel } from '../../types/infrastructure.types'
import type { MateInRoom } from '../../types/types'
import type { UpdateMatesArgs, GetThumbnailHistoryArgs, DeleteThumbnailFromHistoryArgs } from '../../schemas/action.schemas'
import { serializeActiveGame } from '../publicState/publicState'

export interface MatesInRoomContainer {
  storage: RoomStorage
  broadcastChannel: BroadcastChannel
}

/** Most-recently-used thumbnails remembered per persistentId, per room. */
const MAX_THUMBNAIL_HISTORY = 10
/** Temporary room-wide bot cap until bot capacity becomes configurable. */
const MAX_BOTS_PER_ROOM = 1

/** Moves `url` to the front of `history` (inserting it if new), capped at MAX_THUMBNAIL_HISTORY. */
function withThumbnailMostRecent(history: string[], url: string): string[] {
  return [url, ...history.filter((existing) => existing !== url)].slice(0, MAX_THUMBNAIL_HISTORY)
}

/**
 * Creates mate management functions for a room.
 */
export function makeMatesInRoom(container: MatesInRoomContainer) {
  const { storage, broadcastChannel } = container

  /**
   * Update mates for a specific client.
   *
   * Replaces all mates with the given clientId with the provided list.
   * - Mates from other clients are preserved.
   * - For mates that already exist (by mateId), joinedAt is preserved.
   * - For new mates, joinedAt is assigned.
   */
  const updateMates = async (
    args: UpdateMatesArgs
  ): Promise<{ success: boolean; error?: string; mates?: MateInRoom[] }> => {
    const { clientId, mates } = args

    if (!clientId?.trim()) return { success: false, error: 'clientId required' }

    // Validate all mates have required fields and matching clientId
    for (const mate of mates) {
      if (!mate.mateId?.trim()) return { success: false, error: 'mate id required' }
      if (!mate.name?.trim()) return { success: false, error: 'mate name required' }
      if (mate.clientId !== clientId) {
        return { success: false, error: 'mate clientId must match the provided clientId' }
      }
    }

    const state = storage.getState()
    const now = new Date().toISOString()

    // Build a map of existing mates by mateId for joinedAt lookup
    const existingByMateId = new Map(state.mates.map((m) => [m.mateId, m]))

    // Keep mates from other clients
    const otherClientMates = state.mates.filter((m) => m.clientId !== clientId)

    const botCount = [...otherClientMates, ...mates].filter((mate) => mate.isAI).length
    if (botCount > MAX_BOTS_PER_ROOM) {
      return { success: false, error: `a room can have at most ${MAX_BOTS_PER_ROOM} bot` }
    }

    // Remember/apply thumbnails by persistentId, so guests without an account keep
    // their uploaded photos across sessions in this room without any client-side storage.
    // Kept as a capped, most-recently-used list so a mate can switch back to an older photo.
    const thumbnailHistoryByPersistentId = { ...state.thumbnailHistoryByPersistentId }
    for (const mate of mates) {
      if (!mate.persistentId) continue
      if (mate.thumbnailUrl) {
        const history = thumbnailHistoryByPersistentId[mate.persistentId] ?? []
        thumbnailHistoryByPersistentId[mate.persistentId] = withThumbnailMostRecent(history, mate.thumbnailUrl)
        continue
      }
      if (mate.thumbnailUrl === undefined) {
        const mostRecent = thumbnailHistoryByPersistentId[mate.persistentId]?.[0]
        if (mostRecent) mate.thumbnailUrl = mostRecent
      }
    }

    // Convert input mates to MateInRoom, preserving joinedAt if mate existed
    const updatedMates: MateInRoom[] = mates.map((mate) => ({
      ...mate,
      joinedAt: existingByMateId.get(mate.mateId)?.joinedAt ?? now,
    }))

    const newMates = [...otherClientMates, ...updatedMates]

    const newState = { ...state, mates: newMates, thumbnailHistoryByPersistentId }
    storage.setState(newState)

    broadcastChannel.broadcast({
      type: 'roomState',
      data: { activeGame: serializeActiveGame(state.activeGame), mates: newMates },
    })

    return { success: true, mates: updatedMates }
  }

  /**
   * Get the thumbnail history remembered for a persistentId in this room, most-recent-first.
   */
  const getThumbnailHistory = async (args: GetThumbnailHistoryArgs): Promise<{ history: string[] }> => {
    const state = storage.getState()
    return { history: state.thumbnailHistoryByPersistentId?.[args.persistentId] ?? [] }
  }

  /**
   * Remove one photo from the thumbnail history remembered for a persistentId in
   * this room. No-op (returns the unchanged history) if the url isn't in it.
   */
  const deleteThumbnailFromHistory = async (
    args: DeleteThumbnailFromHistoryArgs
  ): Promise<{ history: string[] }> => {
    const { persistentId, url } = args
    const state = storage.getState()
    const history = state.thumbnailHistoryByPersistentId?.[persistentId] ?? []
    const nextHistory = history.filter((existing) => existing !== url)

    if (nextHistory.length !== history.length) {
      const thumbnailHistoryByPersistentId = { ...state.thumbnailHistoryByPersistentId, [persistentId]: nextHistory }
      storage.setState({ ...state, thumbnailHistoryByPersistentId })
    }

    return { history: nextHistory }
  }

  /**
   * Remove all registered mates (and clear any active game).
   * Intended for development/debug flows only.
   */
  const flushMates = async (): Promise<{ success: boolean }> => {
    const state = storage.getState()
    const hadActiveGame = Boolean(state.activeGame)

    const newState = { ...state, mates: [], activeGame: null }
    storage.setState(newState)

    broadcastChannel.broadcast({
      type: 'roomState',
      data: { activeGame: null, mates: [] },
    })
    if (hadActiveGame) {
      broadcastChannel.broadcast({ type: 'gameEnded', data: {} })
    }
    broadcastChannel.broadcast({ type: 'matesFlushed' })
    return { success: true }
  }

  return { updateMates, flushMates, getThumbnailHistory, deleteThumbnailFromHistory }
}
